/**
 * PersistentDashboardMap — mounts once at MainLayout level, persists across
 * route changes to avoid repeated Google Maps JS API loads.
 *
 * Strategy: React Portal into whatever DOM node DashboardPage publishes via
 * DashboardMapAnchorContext. When DashboardPage unmounts (user leaves the
 * dashboard), the anchor becomes null and this component returns null — but
 * React's reconciliation keeps the <MapView> component instance alive so the
 * google.maps.Map object does NOT reload on return.
 */

import { useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { camerasApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { MapView } from '@/components/map/MapView'
import { useDashboardMapAnchor } from '@/contexts/DashboardMapAnchorContext'

export function PersistentDashboardMap() {
  const { anchor, onMarkerClick } = useDashboardMapAnchor()

  const { data: mapPins = [] } = useQuery({
    queryKey: queryKeys.cameras.mapPins(),
    queryFn: () => camerasApi.getMapPins(),
    staleTime: 60_000,
  })

  const markers = useMemo(
    () =>
      mapPins.map((c) => ({
        id: c.id,
        lat: parseFloat(c.latitude),
        lng: parseFloat(c.longitude),
        label: c.name,
        sublabel: c.location ?? undefined,
        groupNames: c.group_names,
        isOnline: c.is_online,
        lastEventType: c.last_event_type,
        lastEventAt: c.last_event_at,
      })),
    [mapPins]
  )

  // No anchor = user is not on the dashboard. Return null so the portal
  // renders nothing — but this component remains mounted in the React tree
  // so React reconciliation preserves the <MapView> instance when the user
  // navigates back and an anchor is published again.
  if (!anchor) return null

  return createPortal(
    <MapView
      markers={markers}
      onMarkerClick={onMarkerClick ?? undefined}
      className="h-full w-full"
    />,
    anchor
  )
}
